import React from 'react';
import { ChildGuidesProps, ResponsiveImage } from '@greystar/ui';
import {
  ImageField,
  ImageFieldValue,
  Item,
  TextField,
  Text as JssText,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import {
  CardCarousel,
  HeaderTextBlock,
  ImageTextCard,
  LayoutContainer,
  PageSection,
  PageSectionContent,
} from '@greystar/designsystem';
import { buildLocationGuidesJsonLd, getDictionaryEntry, getLinkIcon } from '@greystar/lib';
import { useI18n } from 'next-localization';
import { useIsMobile } from '@greystar/ui/src/components/search/search-sort/useIsMobile';

// Sanitize HTML function optimized for Next.js without dependencies
export function sanitizeHtml(html: string): string {
  if (typeof html !== 'string' || html.trim() === '') {
    return '';
  }

  // Remove h1-h6 tags and their content
  let sanitized = html.replace(/<h[1-6][^>]*>[\s\S]*?(?:<\/h[1-6]>|$)/gi, ' ');

  // Remove remaining HTML tags, normalize whitespace, and trim
  sanitized = sanitized
    .replace(/<[^>]+>/g, ' ') // Strip remaining HTML tags
    .replace(/ /g, ' ') // Replace non-breaking spaces
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(dec)) // Decode numeric HTML entities
    .replace(/&\w+;/g, ' ') // Replace named HTML entities with space
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();

  return sanitized;
}

export function truncateToChars(
  text: string,
  charLimit: number = 150 // Default character limit
): string {
  if (typeof text !== 'string' || text.trim() === '') {
    return '';
  }

  // Sanitize HTML to remove h1-h6 tags and other markup
  const sanitizedText = sanitizeHtml(text);

  // If sanitized text is within the limit, return it
  if (sanitizedText.length <= charLimit) {
    return sanitizedText.trim();
  }

  // Truncate to charLimit, preserving whole words
  let truncated = sanitizedText.slice(0, charLimit);
  const lastSpaceIndex = truncated.lastIndexOf(' ');

  // If a space is found within the last 10 characters, truncate there
  if (lastSpaceIndex > charLimit - 10 && lastSpaceIndex < charLimit) {
    truncated = truncated.slice(0, lastSpaceIndex);
  }

  return truncated.trim() + '...';
}

// Define the expected fields for type safety
interface ItemFields {
  image?: ImageFieldValue | { value: string };
  Overview?: { value: string };
  childGuideTitle?: TextField;
  childGuideSubTitle?: TextField;
  'Tile Image AEM'?: { value: string };
  Headline?: { value: string };
  title?: { value: string };
}

const ChildGuides = (props: ChildGuidesProps): JSX.Element => {
  const { t } = useI18n();
  const { isMobileOrTablet } = useIsMobile();
  const { sitecoreContext } = useSitecoreContext();

  // Return early if no items are provided
  if (!props?.fields?.items || props.fields.items.length === 0) {
    return <></>;
  }

  // Extract guideTitle and guideSubTitle from the first item or provide fallback
  const firstItem = props.fields.items[0] as Item & { fields: ItemFields };
  const titleField = sitecoreContext?.route?.fields?.title as TextField | undefined;
  const routeTitle = titleField?.value ?? '';
  const guideTitle = `${getDictionaryEntry(
    t('childguide_title'),
    'Explore places to live in'
  )} ${routeTitle}`;

  const guideSubTitle = firstItem?.fields?.childGuideSubTitle ?? { value: '' };

  const headingStyles = props?.params?.headingStyles as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  return (
    <PageSection topColour={'sky'} borderPosition="top">
      <PageSectionContent hasMaxWidth>
        <HeaderTextBlock direction="reverse">
          <HeaderTextBlock.Header as={headingStyles} header={guideTitle} />
          <HeaderTextBlock.Subtitle
            subtitle={<JssText field={guideSubTitle} />}
            variant="primary"
          />
        </HeaderTextBlock>
        <LayoutContainer
          paddingLeft={isMobileOrTablet ? 'xs' : '3xl'}
          paddingRight={isMobileOrTablet ? 'xs' : '3xl'}
          paddingTop={isMobileOrTablet ? 'md' : '3xl'}
          display={null}
        >
          {' '}
          <LayoutContainer
            paddingLeft={isMobileOrTablet ? 'xs' : '3xl'}
            paddingRight={isMobileOrTablet ? 'xs' : '3xl'}
            display={null}
          >
            {buildLocationGuidesJsonLd(props.fields.items)}
            <CardCarousel
              prevLabel={getDictionaryEntry(t('carousel-card-previous'), 'Carousel Card Previous')}
              nextLabel={getDictionaryEntry(t('carousel-card-next'), 'Carousel Card Next')}
            >
              {props.fields.items.map((item: Item & { fields: ItemFields }) => {
                const sanitizedText = sanitizeHtml(item.fields?.Overview?.value ?? '');
                const truncatedText = truncateToChars(sanitizedText, 170);
                const sxaTags = item.fields?.SxaTags;
                const tagLabel = Array.isArray(sxaTags)
                  ? (sxaTags[0]?.fields?.Title as { value?: string })?.value
                  : undefined;

                return (
                  <ImageTextCard
                    key={item.id}
                    title={item?.fields?.title?.value}
                    bodyText={truncatedText}
                  >
                    <ImageTextCard.Image aspectRatio="4:3">
                      <ResponsiveImage
                        image={item?.fields?.['Tile Image AEM'] as unknown as ImageField}
                        fill={true}
                        sizes="(max-width: 600px) 100vw, (max-width: 1024px) 50vw, 25vw"
                      />
                    </ImageTextCard.Image>
                    <ImageTextCard.MetaData tagLabel={tagLabel ?? 'Location Guide'} />
                    <ImageTextCard.Link
                      href={item.url}
                      variant="secondary"
                      label={getDictionaryEntry(t('navigation-explore'), 'Explore')}
                      icon={getLinkIcon()}
                    />
                  </ImageTextCard>
                );
              })}
            </CardCarousel>
          </LayoutContainer>
        </LayoutContainer>
      </PageSectionContent>
    </PageSection>
  );
};

export default ChildGuides;
